
import React from "react";
import { Helmet } from "react-helmet";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "profile" | "book";
  schema?: object | object[];
}

export function SEOHead({
  title = "Grandpal | Find Nursing Homes in Ireland & Fair Deal Support",
  description = "Grandpal helps families find quality nursing homes across Ireland and navigate the Fair Deal scheme with expert guidance and compassionate support.",
  canonicalUrl,
  ogImage = "https://www.grandpal.ie/lovable-uploads/b67e8743-f24e-45cd-ba77-2a1ae609411c.png",
  ogType = "website",
  schema,
}: SEOHeadProps) {
  // Base URL for the site
  const baseUrl = "https://www.grandpal.ie";
  
  // Current page URL
  const currentUrl = canonicalUrl ? `${baseUrl}${canonicalUrl}` : baseUrl;
  
  // Full image URL
  const fullImageUrl = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;

  // Normalize schema into an array for consistent rendering
  const schemaArray = schema
    ? Array.isArray(schema)
      ? schema
      : [schema]
    : [];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl ?? baseUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl ?? baseUrl} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="Grandpal" />
      <meta property="og:locale" content="en_IE" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@grandpal_ie" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      
      {/* Schema.org JSON-LD — one <script> per schema object for Google compatibility */}
      {schemaArray.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
